import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Route, useHistory } from 'react-router-dom';
import { LOGOUT_ACTION, LOGOUT_ACTION_SESSION_CODE_PARAM } from '../../model/store';
import { routes } from '../../route-list';
import { LoadingScreen } from './LoadingScreen';

const EmailVerificationWrapper: React.FC<{ component: React.ComponentType<any>; [key: string]: any }> = ({
  component: Component,
  ...props
}) => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const history = useHistory();
  const isVerificationPage = window.location.pathname === routes.EMAIL_VERIFICATION;

  useEffect(() => {
    if (isVerificationPage) {
      return;
    }

    if (!isLoading && isAuthenticated && user && !user.email_verified) {
      history.replace(routes.EMAIL_VERIFICATION);
    }
  }, [isAuthenticated, user, history, isVerificationPage, isLoading]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isVerificationPage && isAuthenticated && user && !user.email_verified) {
    return null;
  }

  return <Component {...props} />;
};

/**
 * A wrapper for <Route> that redirects to the login screen
 * if you're not yet authenticated.
 *
 * Reference:
 * https://github.com/auth0/auth0-react/blob/master/examples/cra-react-router/src/ProtectedRoute.tsx
 *
 * @param children
 * @param rest
 * @constructor
 */
export const PrivateRoute = ({ component, ...rest }: React.PropsWithChildren<any>) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    if (params.session === LOGOUT_ACTION_SESSION_CODE_PARAM) {
      dispatch({ type: LOGOUT_ACTION });
    }
  }, [dispatch]);

  return (
    <Route
      render={props => {
        const WrappedComponent = (componentProps: any) => (
          <EmailVerificationWrapper component={component} {...componentProps} />
        );

        let Component = withAuthenticationRequired(WrappedComponent, {
          onRedirecting: () => {
            return <LoadingScreen />;
          },
          // If using a Hash Router, you need to pass the hash fragment as `returnTo`
          returnTo: () => window.location.hash.substr(1),
        });
        return <Component {...props} />;
      }}
      {...rest}
    />
  );
};
